import classNames from "classnames";
import React, { ReactElement, useEffect, useState } from "react";
import { ChannelMode, OnboardingProfile } from "../../../__generated__/api";
import ContactChannelMenu from "../../../assets/img/onboarding/contact-channel-menu.png";
import ContactEmojiChannelMenu from "../../../assets/img/onboarding/contact-emoji-channel-menu.png";
import ContactEmoji from "../../../assets/img/onboarding/contact-emoji.png";
import ContactThreadedChannelMenu from "../../../assets/img/onboarding/contact-threaded-channel-menu.png";
import ContactThreaded from "../../../assets/img/onboarding/contact-threaded.png";
import Help, { ARTICLES } from "../../../components/help/Help";
import Button from "../../../components/theme/button/Button";
import useArticleContext from "../../../contexts/HelpContext";
import Styles from "../Onboarding.module.scss";
import { ChoiceButton, StepProps } from "./base";
import Text from "../../../components/theme/text/Text";

const CONTACT_MODES = [
    {
        id: "threaded",
        title: "Threaded",
        imageSrc: ContactThreaded,
        profile: { channelMode: ChannelMode.Threaded, isEmojiEnabled: false, isChannelMenuEnabled: false } as OnboardingProfile,
        description: "When a user types a message in a channel, Foqal creates a thread and responds automatically.",
        articleId: ARTICLES.CONTACT_EXPLAIN_THREADED,
    },
    {
        id: "threaded-channel-menu",
        title: "Threaded with Channel Menu",
        imageSrc: ContactThreadedChannelMenu,
        profile: { channelMode: ChannelMode.Threaded, isEmojiEnabled: false, isChannelMenuEnabled: true } as OnboardingProfile,
        description: "The Channel Menu sticks to the bottom of the Slack channel and allows users to create requests from forms.",
        articleId: ARTICLES.CONTACT_EXPLAIN_CHANNEL_MENU_INTERNAL,
    },
    {
        id: "emoji",
        title: "Emoji",
        imageSrc: ContactEmoji,
        profile: { channelMode: ChannelMode.Manual, isEmojiEnabled: true, isChannelMenuEnabled: false } as OnboardingProfile,
        description: "Manually convert a Slack message or thread into a ticket using an emoji.",
        articleId: ARTICLES.CONTACT_EXPLAIN_EMOJI,
    },
    {
        id: "emoji-with-channel-menu",
        title: "Emoji with Channel Menu",
        imageSrc: ContactEmojiChannelMenu,
        profile: { channelMode: ChannelMode.Manual, isEmojiEnabled: true, isChannelMenuEnabled: true } as OnboardingProfile,
        description: "The Channel Menu floats at the bottom of the channel and "
                + "allows users to create requests from forms and emojis.",
        articleId: ARTICLES.CONTACT_EXPLAIN_EMOJI,
    },
    {
        id: "channel-menu",
        title: "Channel Menu",
        imageSrc: ContactChannelMenu,
        profile: { channelMode: ChannelMode.Manual, isEmojiEnabled: false, isChannelMenuEnabled: true } as OnboardingProfile,
        description: "The Channel Menu sticks to the bottom of the Slack channel and allows users to create requests from forms.",
        articleId: ARTICLES.CONTACT_EXPLAIN_CHANNEL_MENU_INTERNAL,
    },
];
const DEFAULT_CONTACT_MODE = CONTACT_MODES[0];

export default function OnboardingStep(props: StepProps): ReactElement {
    const [selectedMode, setSelectedMode] = useState<typeof CONTACT_MODES[number]>();
    useEffect(() => {
        const selected = CONTACT_MODES.find(mode => mode.profile.channelMode === props.profile.channelMode
            && mode.profile.isChannelMenuEnabled === props.profile.isChannelMenuEnabled
            && mode.profile.isEmojiEnabled === props.profile.isEmojiEnabled);
        if (selected) {
            setSelectedMode(selected);
        } else {
            props.onNext({ ...props.profile, ...DEFAULT_CONTACT_MODE.profile });
            setSelectedMode(DEFAULT_CONTACT_MODE);
        }
    }, []);

    useArticleContext(ARTICLES.CONTACTED_PAGE);

    return (
        <div>
            <div className={Styles["step-header"]}>
                <h2 className={Styles["step-title"]}>How do you want to be contacted?</h2>

                <p>
                    Choose the method of how do you want people to create requests.
                </p>
            </div>

            <div className={classNames(Styles["step-content"], Styles["list-with-preview"])}>
                <div className={Styles["choice-list"]}>
                    {CONTACT_MODES.map(mode => (
                        <ChoiceButton
                            key={mode.id}
                            arrowDirection="right"
                            isSelected={selectedMode === mode}
                            metricPath={["onboarding", props.stepId, "mode", mode.id]}
                            onSelected={() => {
                                props.onNext({ ...props.profile, ...mode.profile });
                                setSelectedMode(mode);
                            }}
                        >
                            {mode.title}
                        </ChoiceButton>
                    ))}
                </div>
                <div className={Styles["choice-preview"]}>
                    <img src={selectedMode?.imageSrc} alt={selectedMode?.title} />
                    <div className={Styles["choice-preview-content"]}>
                        <h4>{selectedMode?.title}</h4>
                        <p>{selectedMode?.description}</p>
                        <Help
                            articleId={selectedMode?.articleId}
                            buttonType="small-icon"
                            buttonClassName={Styles["choice-learn-more"]}
                            metricPathPrefix={["onboarding", props.stepId, "mode-help", selectedMode?.id].filterNull()}
                            button={<Text as="span" colorVariant="text-link">Learn More</Text>}
                        />

                    </div>
                </div>
            </div>

        </div>
    );
}
